import React, { useEffect, useRef } from 'react';
import { getWizardElement } from '../WizardElements';
import { SingleFieldDisplayProps } from './FormWizardTypes';
import { useSimplifiedUserData } from '@/providers/SimplifiedUserDataProvider';

/**
 * SingleFieldDisplay - Displays one field at a time in a clean, centered layout
 * Used in the Typeform-style wizard flow
 */
export function SingleFieldDisplay({
  field,
  value,
  onChange,
  error,
  autoFocus = true,
}: SingleFieldDisplayProps) {
  const { selectedGame } = useSimplifiedUserData();
  const fieldRef = useRef<HTMLDivElement>(null);

  // Auto-focus the field when it mounts
  useEffect(() => {
    if (autoFocus && fieldRef.current) {
      // Find the first input/textarea/select in the field and focus it
      const focusableElement = fieldRef.current.querySelector<HTMLElement>(
        'input, textarea, select, button'
      );
      if (focusableElement) {
        setTimeout(() => focusableElement.focus(), 100);
      }
    }
  }, [autoFocus, field.id]);

  // Get the wizard element for this field type
  const fieldElement = getWizardElement(
    field,
    value,
    onChange,
    onChange, // Use onChange for onBlur as well
    selectedGame?.id
  );

  return (
    <div className="w-full max-w-3xl mx-auto px-4">
      {/* Question Label */}
      <div className="mb-8">
        <label className="block text-3xl font-bold mb-2">
          {field.label}
          {field.required && (
            <span className="text-red-500 ml-2">*</span>
          )}
        </label>
        
        {/* Documentation/Help Text */}
        {field.documentation && (
          <div 
            className="text-muted-foreground text-lg mt-3"
            dangerouslySetInnerHTML={{ __html: field.documentation }}
          />
        )}
        
        {/* Placeholder as help text if no documentation */}
        {!field.documentation && field.placeholder && (
          <p className="text-muted-foreground text-lg mt-3">
            {field.placeholder}
          </p>
        )}
      </div>

      {/* Field Input */}
      <div ref={fieldRef} className="mb-4">
        {fieldElement}
      </div>

      {/* Error Message */}
      {error && (
        <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-red-600 text-sm flex items-center gap-2">
            <svg 
              className="w-5 h-5" 
              fill="currentColor" 
              viewBox="0 0 20 20"
            >
              <path 
                fillRule="evenodd" 
                d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" 
                clipRule="evenodd" 
              />
            </svg>
            {error}
          </p>
        </div>
      )}
    </div>
  );
}
